/**
 * Where the chat pane must scroll when the send it is holding near the top has MOVED — pure, no imports, so
 * it can be unit-tested without the chat's dependency graph (see the orion-web CLAUDE.md "Tests" section).
 *
 * The send glide (`MessagesPanel/use-scroll.ts`) scrolls the just-sent row to `rowTop - inset` and then holds
 * it there by resizing the reserve below the reply, never by writing a scroll position — which is right while
 * the content ABOVE the row stays put, because then the row never moves. The browser's own scroll anchoring is
 * off for the whole hold (it anchored to the loader and pushed the send off), so nothing else compensates when
 * the content above DOES change: a handoff send into an existing chat (the site review's "Send review", a
 * pack's Edit) paints that chat's cached rows and sends in the same breath, and the rows above then grow as
 * their images and cards lay out, or are re-read under it. The row slid down past the fold, the pane kept
 * showing the agent's earlier reply, and the scroll-to-latest button was the only sign the send had gone
 * (reported from a phone, 2026-10-24).
 *
 * So: when the row is more than a pixel from where the glide aimed it, aim again at its new top. If the pane
 * had already landed, jump — the content above just moved, and an instant write is what keeps the row still on
 * screen, the same thing scroll anchoring does. If the glide is still running, retarget it smoothly.
 * Returns null when the row is where it was aimed (every ordinary send, whose reply only grows BELOW it).
 */
export function repinMovedSend({
  rowTop,
  aimedRowTop,
  scrollTop,
  inset,
}: {
  /** The pinned row's `offsetTop` now. */
  rowTop: number;
  /** Its `offsetTop` when the pane was last aimed at it; null before the glide has run. */
  aimedRowTop: number | null;
  /** The pane's current `scrollTop`. */
  scrollTop: number;
  /** Breathing room above the pinned row (`ANCHOR_TOP_INSET`). */
  inset: number;
}): { top: number; smooth: boolean } | null {
  if (aimedRowTop === null || Math.abs(rowTop - aimedRowTop) <= 1) return null;
  const aimed = Math.max(0, aimedRowTop - inset);
  const landed = Math.abs(scrollTop - aimed) <= 1;
  return { top: Math.max(0, rowTop - inset), smooth: !landed };
}
